import { useMemo } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { Textarea } from "@/components/ui/textarea"
import { MessageCircle, MessageSquare, MessageSquareDot } from "lucide-react";

type MoodOption = {
  label: string;
  color: string;
};

type CalendarProps = {
  month: number;
  year: number;
  moods: Record<string, { color?: string; comment?: string }>;
  moodOptions: MoodOption[];
  onMoodSelect: (day: number, moodColor: string) => void;
  onDayCommentChange: (day: number, comment: string) => void;
};

const DAYS = ["L", "M", "M", "J", "V", "S", "D"];

export function Calendar({ month, year, moods, moodOptions, onMoodSelect, onDayCommentChange }: CalendarProps) {
  const { blanksBefore, days, blanksAfter } = useMemo(() => {
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const lastDayOfMonth = new Date(year, month + 1, 0).getDay();

    const startingDayIndex = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    let trailingDays = 7 - lastDayOfMonth;

    if (lastDayOfMonth == 0) {
      trailingDays = 0;
    }

    const daysInMonth = new Date(year, month + 1, 0).getDate();

    return {
      blanksBefore: Array.from({ length: startingDayIndex }),
      days: Array.from({ length: daysInMonth }, (_, i) => i + 1),
      blanksAfter: Array.from({ length: trailingDays }),
    };
  }, [month, year]);

  const getDayData = (day: number) => {
    const dateKey = `${year}-${month}-${day}`;
    return moods[dateKey] || {};
  }

  return (
    <div className="w-full">
      <div className="grid grid-cols-7 mb-2">
        {DAYS.map((day, i) => (
          <div key={i} className="text-center font-bold text-muted-foreground py-2">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-2">
        {blanksBefore.map((_, i) => (
          <div key={`blank-before-${i}`} className="aspect-square" />
        ))}

        {days.map((day, i) => {
          const { color: moodColor, comment } = getDayData(day);
          return (
            <Popover key={i}>
              <PopoverTrigger asChild>
                <div
                  className={cn(
                    "aspect-square relative p-1 rounded-xl shadow-xs border flex flex-col hover:opacity-80 transition-all cursor-pointer group overflow-hidden",
                    moodColor ? moodColor : "bg-card hover:bg-accent/5"
                  )}
                >
                  <span className={cn(
                    "absolute inset-0 flex items-center justify-center text-2xl lg:text-4xl font-black select-none pointer-events-none transition-colors",
                    moodColor ? "text-white/20" : "text-muted group-hover:text-muted/20"
                  )}>
                    {day}
                  </span>
                  {comment && (
                    <MessageSquareDot className="absolute bottom-0 right-1 w-4" />
                  )}
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-4 space-y-4">
                <div className="space-y-2">
                  {day} {new Date(0, month).toLocaleString("fr-FR", { month: "long" })}
                  <h4 className="font-medium leading-none mt-2">Humeur</h4>
                  <div className="flex gap-2 flex-wrap">
                    {moodOptions.map((option) => (
                      <button
                        key={option.label}
                        className={cn("w-8 h-8 rounded-full transition-transform hover:scale-110", option.color, moodColor === option.color && "ring-2 ring-offset-2 ring-black/20 dark:ring-white/20")}
                        title={option.label}
                        onClick={() => onMoodSelect(day, option.color)}
                      />
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <h4 className="font-medium leading-none">Note du jour</h4>
                  <Textarea
                    placeholder="Commentaire du jour"
                    className="resize-none h-20"
                    value={comment || ""}
                    onChange={(e) => onDayCommentChange(day, e.target.value)}
                  />
                </div>
              </PopoverContent>
            </Popover>
          )
        })}

        {blanksAfter.map((_, i) => (
          <div key={`blank-after-${i}`} className="aspect-square" />
        ))}
      </div>
    </div>
  );
}
